/**
 * Authentication Context
 *
 * Provides global auth state and functions throughout the app.
 * - Tracks current user
 * - Handles login/logout
 * - Checks auth status on app load
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  User,
  LoginRequest,
  login as apiLogin,
  loginWithGoogle as apiLoginWithGoogle,
  logout as apiLogout,
  getCurrentUser,
} from '../api/auth';
import { getAccessToken } from '../api/client';

// =============================================================================
// Types
// =============================================================================

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginRequest) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated
  const isAuthenticated = !!user;

  /**
   * Fetch current user from API
   */
  const refreshUser = useCallback(async () => {
    try {
      const { user } = await getCurrentUser();
      setUser(user);
    } catch {
      // If fetching user fails, clear the user state
      setUser(null);
    }
  }, []);

  /**
   * Login responses carry the token payload's orgs, which lack the per-org
   * details /auth/me adds (timezone, tier, SMS flag). Fetch them right after
   * login so dates render in the org's zone from the first page. Unlike
   * refreshUser, a failure here keeps the session — the login itself worked.
   */
  const refreshOrgDetails = useCallback(() => {
    getCurrentUser()
      .then(({ user }) => setUser(user))
      .catch(() => {});
  }, []);

  /**
   * Check auth status on mount
   */
  useEffect(() => {
    async function checkAuth() {
      const token = getAccessToken();

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        await refreshUser();
      } catch {
        // Token is invalid or expired
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    checkAuth();
  }, [refreshUser]);

  /**
   * Login function
   */
  const login = useCallback(async (data: LoginRequest) => {
    const response = await apiLogin(data);
    setUser(response.user);
    refreshOrgDetails();
  }, [refreshOrgDetails]);

  /**
   * Login with a Google ID token (from Google Identity Services)
   */
  const loginWithGoogle = useCallback(async (idToken: string) => {
    const response = await apiLoginWithGoogle(idToken);
    setUser(response.user);
    refreshOrgDetails();
  }, [refreshOrgDetails]);

  /**
   * Logout function
   */
  const logout = useCallback(() => {
    apiLogout();
    setUser(null);
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    loginWithGoogle,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
